import React from 'react';

import { akEditorDefaultLayoutWidth } from '@atlaskit/editor-shared-styles';

import type { EditorPluginNativeEmbedsPlugin } from './nativeEmbedsPluginType';
import {
	createCardToNativeEmbedTransform,
	cardToNativeEmbedNode,
	insertedContentToNativeEmbedNode,
} from './pm-plugins/actions/card-transform';
import { createPlugin, pluginKey } from './pm-plugins/plugin-state';
import { getToolbarConfig } from './pm-plugins/ui/floating-toolbar';
import { ResizableNativeEmbed } from './ui/ResizableNativeEmbed';

export const nativeEmbedsPlugin: EditorPluginNativeEmbedsPlugin = ({ api, config }) => {
	// Register the native embed transformers with the card plugin so that
	// embedCard nodes are automatically converted to native embed extensions
	// during URL resolution and appearance switching.
	api?.card?.actions?.registerEmbedCardTransformer({
		embedCardNodeTransformer: cardToNativeEmbedNode,
		createEmbedCardTransformCommand: createCardToNativeEmbedTransform,
	});

	// Register the native embed transformer with the AI plugin so that
	// inserted content nodes can be automatically converted to native embed
	// extensions during AI content insertion.
	api?.aiExperience?.actions?.registerInsertedContentNodeTransformer?.(
		insertedContentToNativeEmbedNode,
	);

	return {
		name: 'editorPluginNativeEmbeds',
		actions: {
			cardToNativeEmbedNode: cardToNativeEmbedNode,
			createCardToNativeEmbedTransform: createCardToNativeEmbedTransform,
		},
		pmPlugins() {
			const analyticsApi = api?.analytics?.actions;
			return [
				{
					name: 'nativeEmbedsPluginState',
					plugin: () => createPlugin(analyticsApi),
				},
			];
		},

		getSharedState(editorState) {
			if (!editorState) {
				return undefined;
			}
			const pmState = pluginKey.getState(editorState);
			if (!pmState) {
				return undefined;
			}
			return {
				width: pmState.resizingWidth || akEditorDefaultLayoutWidth,
				isResizing: pmState.isResizing,
				selectedEmbedAlignment: pmState.selectedEmbedAlignment,
				selectedEmbedPos: pmState.selectedEmbedPos,
			};
		},
		contentComponent(params) {
			if (!params.editorView) {
				return null;
			}

			return (
				<ResizableNativeEmbed
					disabled={params.disabled}
					editorView={params.editorView}
					pluginInjectionApi={api}
					appearance={params.appearance}
					containerElement={params.containerElement}
					editorActions={params.editorActions}
					eventDispatcher={params.eventDispatcher}
					popupsMountPoint={params.popupsMountPoint}
					providerFactory={params.providerFactory}
					wrapperElement={params.wrapperElement}
				/>
			);
		},
		pluginsOptions: {
			floatingToolbar: getToolbarConfig({
				api,
				linkPickerOptions: config?.linkPickerOptions,
				lpLinkPicker: config?.lpLinkPicker,
			}),
		},
	};
};
